<template>
  <div>
    <CommonBreadCrumbs v-if="currentRoute?.showBreadcrumbs ?? true" :data="breadcrumbs" />

    <PageHeader />
    <Component :is="currentRoute?.component" />

    <CommonLiveChatBlock v-if="currentRoute?.showChatComponent" class="mx-4 mb-4 mt-8 max-w-[1060px] lg:mx-auto" />
  </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from "~/stores/global";
import AccountSettings from "./protected/views/account-settings/index.vue";
import OrderHistory from "./protected/views/order-history/index.vue";
import AccountSecurity from "./protected/views/security/index.vue";
import AuthContainer from "./public/components/auth-container.vue";
import PageHeader from "./public/components/page-header.vue";
import PasswordReset from "./public/views/password-reset.vue";
import ResetPassword from "./public/views/reset-password.vue";
import VerifyEmail from "./public/views/verify-email.vue";
import VerifyUpdatedEmail from "./public/views/verify-updated-email.vue";

const globalStore = useGlobalStore();
const route = useRoute();
const { t, te } = useI18n();

const breadcrumbs = [
  {
    title: t(`pages.account.${route.params.page}.title`),
    to: "/account/" + route.params.page,
  },
];

if (route.params.page === "settings") {
  breadcrumbs.unshift({
    title: t("pages.account.dashboard.title"),
    to: "/account",
  });
}

definePageMeta({
  middleware: [
    (to) => {
      const page = String(to.params.page);
      if (["verify-email", "verify-updated-email"].includes(page)) {
        to.meta.layout = "confirmation-notification" as any;
      } else {
        to.meta.layout = "default";
      }
    },
    (to) => {
      const gatedPages = ["login", "signup", "password-reset", "reset-password"];
      const page = String(to.params.page);

      if (!gatedPages.includes(page)) return;

      const config = useRuntimeConfig().public;
      const accountEnabledLocales = config.accountEnabledLocales ? String(config.accountEnabledLocales).split(",") : [];
      const locale = useNuxtApp().$i18n.locale.value;

      if (!accountEnabledLocales.includes(locale)) {
        return navigateTo(localeHomePath(locale), {
          redirectCode: 302,
          external: false,
        });
      }
    },
  ],
});

interface AccountRoute {
  key: string;
  component: unknown;
  showChatComponent: boolean;
  showBreadcrumbs?: boolean;
}
const ROUTES: AccountRoute[] = [
  {
    key: "login",
    component: AuthContainer,
    showChatComponent: true,
  },
  {
    key: "signup",
    component: AuthContainer,
    showChatComponent: true,
  },
  {
    key: "password-reset",
    component: PasswordReset,
    showChatComponent: true,
  },
  {
    key: "reset-password",
    component: ResetPassword,
    showChatComponent: true,
  },
  {
    key: "settings",
    component: AccountSettings,
    showChatComponent: false,
  },
  {
    key: "security",
    component: AccountSecurity,
    showChatComponent: true,
  },
  {
    key: "order-history",
    component: OrderHistory,
    showChatComponent: true,
  },
  {
    key: "verify-email",
    component: VerifyEmail,
    showChatComponent: false,
    showBreadcrumbs: false,
  },
  {
    key: "verify-updated-email",
    component: VerifyUpdatedEmail,
    showChatComponent: false,
    showBreadcrumbs: false,
  },
];

const currentRoute = computed(() => ROUTES.find((x) => x.key === route.params.page));

const ACCOUNT_META_PAGES = ["login", "signup", "password-reset"];

useHead(() => {
  const page = String(route.params.page);
  if (!ACCOUNT_META_PAGES.includes(page)) {
    return {};
  }

  let titleKey = `pages.account.${page}.meta.title`;
  let descriptionKey = `pages.account.${page}.meta.description`;

  const isUk = globalStore.locale === "en-gb";

  if (!isUk) {
    titleKey = `pages.account.${page}.meta.titleIE`;
    descriptionKey = `pages.account.${page}.meta.descriptionIE`;
  }

  if (!te(titleKey) && !te(descriptionKey)) {
    return {};
  }

  const title = te(titleKey) ? t(titleKey) : null;
  const description = te(descriptionKey) ? t(descriptionKey) : null;

  const meta = [
    title ? { name: "title", content: title } : null,
    description ? { name: "description", content: description } : null,
  ].filter(Boolean);

  return {
    ...(title ? { title } : {}),
    meta,
  };
});
</script>
